// src/components/HearingRecordPanel.js
//
// T5 / D-12 (2026-10-15) - the hearing record on the case page: objections made on the record and the rulings on them,
// testimony and cross-examination points, exhibits, recesses, and corrections. src\data\hearingRecord.js validates;
// this file draws, and appends to the case's hearingRecord[] with arrayUnion (never rewritten). Officers of the case
// write it (canAct, as for the hearing acts); the member's own-case arm is refused by the rules (ownerOpenEdit).
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ENTRY_KINDS, KINDS, OFFERED_BY, RULINGS, RULING_LABELS, TEXT_MAX, nextSeq, objectionsWithRulings, planEntry } from '../data/hearingRecord';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };

export default function HearingRecordPanel({ disciplineCase, caseId, user, canAct }) {
  const k = disciplineCase || {};
  const entries = (Array.isArray(k.hearingRecord) ? k.hearingRecord : []).slice().sort((a, b) => (a.seq || 0) - (b.seq || 0));
  const heldOn = ((Array.isArray(k.hearingEvents) ? k.hearingEvents : []).filter(e => e && e.kind === 'hearing_held').pop() || {}).on || '';
  const [form, setForm] = useState({ kind: 'objection', on: heldOn });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const objs = objectionsWithRulings(entries);
  const unruled = objs.filter(o => o.unruled).length;

  const save = async () => {
    const p = planEntry(form, nextSeq(entries));
    if (!p.ok) { setMsg({ ok: false, text: p.errors.join(' ') }); return; }
    setBusy(true); setMsg(null);
    try {
      const entry = { ...p.entry, recordedBy: (user && (user.name || user.email)) || '', recordedByUid: (auth.currentUser && auth.currentUser.uid) || '' };
      await updateDoc(doc(db, 'disciplineCases', caseId), {
        hearingRecord: arrayUnion(entry), updatedAt: serverTimestamp(),
        updatedBy: (auth.currentUser && auth.currentUser.uid) || '', _auditActor: (user && user.email) || 'unknown',
        _auditReason: 'Hearing record entry #' + entry.seq + ': ' + ENTRY_KINDS[entry.kind].label,
      });
      setForm({ kind: form.kind, on: form.on });
      setMsg({ ok: true, text: 'Entry #' + entry.seq + ' recorded.' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-title">The hearing record - what was said and done on the record</div>
      <div className="card-subtitle" style={{ marginBottom: 8 }}>
        Objections and the rulings on them, testimony, exhibits and recesses. An objection not raised at the hearing is usually lost on appeal. Entries are numbered and never rewritten; a correction is its own entry.
      </div>
      {objs.length ? (
        <div style={{ fontSize: 12, fontWeight: 700, color: unruled ? C.accent : C.ok, marginBottom: 8 }}>
          {objs.length} objection{objs.length === 1 ? '' : 's'} on the record - {unruled ? unruled + ' without a ruling' : 'every one ruled on'}
        </div>
      ) : null}
      {entries.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>Nothing is on the hearing record yet.</div> : (
        <div className="table-wrapper"><table>
          <thead><tr><th>#</th><th>Date</th><th>Entry</th><th>Detail</th><th>Transcript page</th><th>Recorded by</th></tr></thead>
          <tbody>{entries.map(e => (
            <tr key={e.seq}>
              <td>{e.seq}</td><td>{e.on}{e.time ? ' ' + e.time : ''}</td>
              <td>{ENTRY_KINDS[e.kind] ? ENTRY_KINDS[e.kind].label : e.kind}
                {e.kind === 'ruling' ? ' on #' + e.onObjection + ': ' + (RULING_LABELS[e.ruling] || e.ruling) : ''}
                {e.kind === 'correction' ? ' (corrects #' + e.corrects + ')' : ''}
                {e.witness ? ' - ' + e.witness : ''}{e.exhibitNo ? ' - Exhibit ' + e.exhibitNo + ' (' + e.offeredBy + ')' + (e.objectedTo ? ', objected to' : '') : ''}</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{e.text}</td><td>{e.pageRef || ''}</td><td>{e.recordedBy || ''}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {canAct ? (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-start' }}>
          <select aria-label="Which hearing act to record" className="form-select" value={form.kind} onChange={e => set('kind', e.target.value)}>
            {KINDS.map(x => <option key={x} value={x}>{ENTRY_KINDS[x].label}</option>)}
          </select>
          <input aria-label="Date" className="form-input" type="date" value={form.on || ''} onChange={e => set('on', e.target.value)} />
          <input aria-label="Time (optional)" className="form-input" placeholder="Time (optional)" value={form.time || ''} onChange={e => set('time', e.target.value)} style={{ width: 110 }} />
          {form.kind === 'ruling' ? (<>
            <select aria-label="On objection" className="form-select" value={form.onObjection || ''} onChange={e => set('onObjection', e.target.value)}>
              <option value="">On objection #...</option>
              {objs.map(o => <option key={o.objection.seq} value={o.objection.seq}>#{o.objection.seq} {String(o.objection.text).slice(0, 40)}</option>)}
            </select>
            <select aria-label="Ruling" className="form-select" value={form.ruling || ''} onChange={e => set('ruling', e.target.value)}>
              <option value="">Ruling...</option>
              {RULINGS.map(r => <option key={r} value={r}>{RULING_LABELS[r]}</option>)}
            </select>
          </>) : null}
          {form.kind === 'testimony' || form.kind === 'cross' ? <input aria-label="Witness" className="form-input" placeholder="Witness" value={form.witness || ''} onChange={e => set('witness', e.target.value)} /> : null}
          {form.kind === 'exhibit' ? (<>
            <input aria-label="Exhibit no." className="form-input" placeholder="Exhibit no." value={form.exhibitNo || ''} onChange={e => set('exhibitNo', e.target.value)} style={{ width: 100 }} />
            <select aria-label="Offered by" className="form-select" value={form.offeredBy || ''} onChange={e => set('offeredBy', e.target.value)}>
              <option value="">Offered by...</option>{OFFERED_BY.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <label style={{ fontSize: 12, color: C.text }}><input aria-label="Objected to" type="checkbox" checked={!!form.objectedTo} onChange={e => set('objectedTo', e.target.checked)} /> objected to</label>
          </>) : null}
          {form.kind === 'correction' ? <input aria-label="objected to" className="form-input" placeholder="Corrects entry #" value={form.corrects || ''} onChange={e => set('corrects', e.target.value)} style={{ width: 130 }} /> : null}
          <input aria-label="Transcript page (optional)" className="form-input" placeholder="Transcript page (optional)" value={form.pageRef || ''} onChange={e => set('pageRef', e.target.value)} style={{ width: 170 }} />
          <textarea aria-label="What was said or done" className="form-input" rows={2} maxLength={TEXT_MAX} placeholder="What was said or done" value={form.text || ''} onChange={e => set('text', e.target.value)} style={{ flexBasis: '100%' }} />
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Recording...' : 'Record entry'}</button>
          {msg ? <span style={{ fontSize: 12, color: msg.ok ? C.ok : C.bad }}>{msg.text}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
